import { getRefListItemsbyName } from "@/util/common_function";

// Merges externally-fetched flag rows (e.g. GET flags/jacket/{jacketId})
// into a cdINVOLVEMENTS array by invId. A cdInvolvements row with no
// matching flagsData entry is kept as-is. A matched row keeps every field it
// already has - only relatedRecordSummary and summaryDescription are pulled
// in from flagsData (both are backend-computed and not reliably present on
// the record's own saved cdInvolvements row - e.g. a warrant's own
// cdInvolvements never carries invDesc/summaryDescription at all, only
// flags/jacket/{jacketId} does, which is why the Flags dialog's Summary
// Description column came back empty there even though the API had it - see
// getFlagTableRows below, which now prefers this field). Any flagsData row
// with NO matching cdInvolvements row (invId not already present) is
// appended as-is - flags/jacket/{jacketId} is the authoritative list of
// what's actually on the jacket, so a flag it knows about that the booking
// payload's own cdInvolvements doesn't (e.g. a Classification questionnaire's
// flag, which now lives on that questionnaire's own nested cdInvolvement
// rather than the booking-root array - see
// ClassificationQuestionnairesList.jsx) must still show up here rather than
// silently being dropped.
// Shared by every module that re-fetches a jacket's live flags after loading
// a booking/warrant/visitor log record (Bookings, Warrants, VisitorLog) -
// see pages/Bookings/index.jsx for the original call site.
//
// This used to spread the whole flagsData row over c (flagsData fields
// winning). Confirmed bug (2026-08-19): adding a new flag from the Warrants or
// VisitorLog module and saving re-stamps every OTHER non-In-Jail
// cdINVOLVEMENTS row under that jacket with the current module's own
// RelatedObjectType/RelatedObjectID on the backend (e.g. a Bookings-origin
// flag's relatedObjectID silently becomes the just-saved WarrantID/
// VisitorLogID) - see the "Bookings"/"WarrantRoots" case comment in
// getRelatedRecordSummary below for the matching Bookings-side symptom.
// flagsData (GET flags/jacket/{jacketId}) reflects that same corrupted
// RelatedObjectType/RelatedObjectID, so spreading it over an
// already-correct row silently overwrote good data with bad. The backend
// separately stamps computed relatedRecordSummary/summaryDescription values
// directly onto each row that aren't affected by that bug, so those are the
// only fields pulled onto an EXISTING row - everything else (including
// relatedObjectType/relatedObjectID) stays exactly as the record already
// has it. A brand new row (no existing cdInvolvements counterpart) has
// nothing to protect, so it goes in untouched.
export const mergeFlagsIntoInvolvements = (cdInvolvements, flagsData) => {
  const flagsByInvId = new Map(
    (flagsData || []).map((fd) => [String(fd?.invId), fd]),
  );
  const merged = (cdInvolvements || []).map((c) => {
    const newFlagData = flagsByInvId.get(String(c?.invId));
    if (!newFlagData) return c;
    flagsByInvId.delete(String(c?.invId));
    const overlay = {};
    if (newFlagData.relatedRecordSummary !== undefined) {
      overlay.relatedRecordSummary = newFlagData.relatedRecordSummary;
    }
    if (newFlagData.summaryDescription !== undefined) {
      overlay.summaryDescription = newFlagData.summaryDescription;
    }
    return Object.keys(overlay).length ? { ...c, ...overlay } : c;
  });
  // Whatever's left in the map had no cdInvolvements counterpart at all.
  return [...merged, ...flagsByInvId.values()];
};

// Fields flags/jacket/{jacketId} adds to a cdINVOLVEMENTS row for display
// only - none of them exist on the write DTO (see bookings.modal.js's
// cdINVOLVEMENTS field list: invId/invCreatedDate/invCreatedBy/
// invExpireDate/invObjType/invObjId/invTypeId/invDesc/invDeleteDate/
// invDeleteBy/linkedObjectType/relatedObjectID/relatedObjectType/
// flagTypeID/noteTypeId/dangerTypeId/warrantStateSubmissionID/atState/
// gangTypeId/medicalTypeId). mergeFlagsIntoInvolvements above stamps these
// onto bookings.cdInvolvements so the flag badges/dialog can show them (see
// getFlagTableRows), but every save payload is built by cloning/spreading
// that same state - use stripFlagDisplayFields/sanitizeInvolvementsForSave
// right before a cdInvolvements array goes into a save payload, or these
// leak into the request as unknown fields the backend was never going to
// persist anyway (confirmed 2026-08-21: a saved summaryDescription didn't
// come back on the next flags/jacket/{jacketId} GET).
const FLAG_DISPLAY_ONLY_FIELDS = [
  "flagLabel",
  "summaryDescription",
  "relatedRecordSummary",
  "deletable",
];

export const stripFlagDisplayFields = (inv) => {
  if (!inv) return inv;
  const clean = { ...inv };
  FLAG_DISPLAY_ONLY_FIELDS.forEach((key) => delete clean[key]);
  return clean;
};

// sanitizeInvolvementsForSave (below) needs getFlagSubTypeLabel/pick, which
// aren't defined until further down this file - fine, since this is only
// ever called from inside sanitizeInvolvementsForSave's own function body,
// long after the whole module has finished evaluating.
// Never drop rows here: leaving the flags/jacket-appended rows out of a
// Warrants save deleted every flag on the jacket (2026-09-30) - the backend
// appears to treat the list as the jacket's full set of flags.
export const sanitizeInvolvementsForSave = (cdInvolvements) =>
  (cdInvolvements || [])
    .map(backfillInvDescFromSummary)
    .map(stripFlagDisplayFields);

// Every active flag on this booking, from all the places they can now live:
// bookings.cdInvolvements (root - Folder Flags, Danger/Gang/Notes side
// effects, Warrants, Civil Papers, In Jail) and each Classification/Medical
// questionnaire's own nested cdInvolvement (singular - see
// ClassificationQuestionnairesList's and MedicalQuestionnaireList's
// setJacketFlagsForQuestionnaire; matches the API's
// InmateClassificationQuestionnaires.cdInvolvement /
// MedicalQuestionnaires.cdInvolvement shapes). Anything that needs "every
// active flag" (the header flag badges, the Flags dialog) should read
// through this instead of bookings.cdInvolvements directly, or it'll miss
// flags a questionnaire's side effects just set.
//
// The booking GET returns a saved questionnaire's flags as `cdInvolvements`
// (plural), while the save DTO and every in-session edit use `cdInvolvement`
// (singular) - read through getQuestionnaireInvolvements so both are seen.
// A saved questionnaire flag also shows up at the root, appended there by
// mergeFlagsIntoInvolvements from flags/jacket/{jacketId} - but that copy has
// no sub-type field (medicalTypeId etc.), so the Summary Description lost its
// "[Type]. " prefix after save. Rows sharing an invId are merged into one:
// the nested row's fields, plus any non-empty fields from the root copy
// (summaryDescription/relatedRecordSummary/deletable).
export const getQuestionnaireInvolvements = (questionnaire) =>
  questionnaire?.cdInvolvement ?? questionnaire?.cdInvolvements ?? [];

// Root cdInvolvements minus any row (by invId) that also lives on a
// Classification/Medical questionnaire's own nested list - see the booking
// save in pages/Bookings/index.jsx for why those must not be re-sent at root.
export const excludeQuestionnaireInvolvements = (cdInvolvements, bookings) => {
  const nestedInvIds = new Set(
    [
      ...(bookings?.inmateClassificationQuestionnaires || []),
      ...(bookings?.medicalQuestionnaires || []),
    ]
      .flatMap(getQuestionnaireInvolvements)
      .map((inv) => inv?.invId)
      .filter((id) => id !== undefined && id !== null)
      .map(String),
  );
  if (!nestedInvIds.size) return cdInvolvements || [];
  return (cdInvolvements || []).filter(
    (inv) => inv?.invId == null || !nestedInvIds.has(String(inv.invId)),
  );
};

export const getAllInvolvements = (bookings) => {
  const root = bookings?.cdInvolvements || [];
  const nested = [
    ...(bookings?.inmateClassificationQuestionnaires || []),
    ...(bookings?.medicalQuestionnaires || []),
  ].flatMap(getQuestionnaireInvolvements);

  const result = [];
  const indexByInvId = new Map();
  [...nested, ...root].forEach((inv) => {
    const key = inv?.invId;
    if (key === undefined || key === null || !indexByInvId.has(String(key))) {
      if (key !== undefined && key !== null) {
        indexByInvId.set(String(key), result.length);
      }
      result.push(inv);
      return;
    }
    const index = indexByInvId.get(String(key));
    const filled = Object.fromEntries(
      Object.entries(inv).filter(([, v]) => v !== undefined && v !== null && v !== ""),
    );
    result[index] = { ...result[index], ...filled };
  });
  return result;
};

// The flags to SHOW (header flag badges, Flags dialog, Jacket Details flag
// panels): saved flags come only from GET flags/jacket/{jacketId} - a saved
// row on the record's own cdInvolvements (or a questionnaire's nested list)
// that flags/jacket doesn't return is not shown. A flag added this session
// has no invId yet and can't be in flags/jacket until Save, so it's kept; a
// flag deleted this session is already gone from getAllInvolvements.
// jacketFlags is state.bookings.jacketFlags ({ jacketId, flags }), set by
// every loader that calls flags/jacket. When it's missing or belongs to a
// different jacket (a module that loads a record without calling
// flags/jacket), this falls back to getAllInvolvements unfiltered.
// Display only - save payloads must keep reading cdInvolvements itself (see
// sanitizeInvolvementsForSave: never drop rows there).
export const getDisplayInvolvements = (bookings, jacketFlags) => {
  const all = getAllInvolvements(bookings);
  const jacketId = bookings?.jacketId ?? bookings?.jacketID;
  if (
    !jacketFlags ||
    jacketId === undefined ||
    jacketId === null ||
    String(jacketFlags.jacketId) !== String(jacketId)
  ) {
    return all;
  }
  const jacketInvIds = new Set(
    (Array.isArray(jacketFlags.flags) ? jacketFlags.flags : []).map((f) =>
      String(f?.invId),
    ),
  );
  return all.filter(
    (inv) =>
      inv?.invId === undefined ||
      inv?.invId === null ||
      jacketInvIds.has(String(inv.invId)),
  );
};

// cdINVOLVEMENTS rows relevant to a given flag type (e.g. "FLAGTYPES_DANGER"),
// matched the same way the flag badge popover (InmateFlag) does.
export const getRelevantInvolvements = (cdInvolvements, flagTypeConstant) => {
  if (!cdInvolvements || !cdInvolvements.length) return [];

  const filterFT = getRefListItemsbyName("FlagTypes")?.filter((f) => {
    return f?.constantName === flagTypeConstant;
  });
  if (!filterFT || !filterFT.length) return [];

  const refId = filterFT[0].refListItemGlobalId;
  return cdInvolvements.filter((inv) => {
    return (
      inv.flagTypeID === refId ||
      inv.dangerTypeId === refId ||
      inv.gangTypeId === refId ||
      inv.medicalTypeId === refId ||
      inv.noteTypeId === refId ||
      inv.civicTypeId === refId
    );
  });
};

// Only these flag types carry a sub-type reference list (Danger Type, Gang
// Type, Medical Type, Note Type); Warrants/Civil Papers/In Jail don't.
export const getFlagSubTypeFieldInfo = (flagTypeConstant) => {
  if (!flagTypeConstant) return null;
  const parts = flagTypeConstant.split("_");
  if (parts.length < 2) return null;
  let flagKey = parts[1].toLowerCase();
  flagKey = flagKey === "notes" ? "note" : flagKey;
  const fieldName = `${flagKey}TypeId`;
  const refListName = `${flagKey.toUpperCase()}_Type`;
  if (!["danger", "gang", "medical", "note"].includes(flagKey)) return null;
  return { fieldName, refListName };
};

// The flag's sub-type label (e.g. Danger Type "Escape risk", Gang Type "Bloods").
export const getFlagSubTypeLabel = (flagTypeConstant, item) => {
  const fieldInfo = getFlagSubTypeFieldInfo(flagTypeConstant);
  if (!fieldInfo || !item || !(fieldInfo.fieldName in item)) return "";

  const refItems = getRefListItemsbyName(fieldInfo.refListName) || [];
  const match = refItems.find(
    (r) => r.refListItemGlobalId === item[fieldInfo.fieldName],
  );
  return match ? match.defaultString || match.longString || "" : "";
};

const pick = (obj, ...keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

// A record's own saved cdInvolvements can have no invDesc at all (e.g. a
// Warrant's own cdInvolvements never carries it - see the comment above
// mergeFlagsIntoInvolvements) even though flags/jacket/{jacketId} stamped a
// computed summaryDescription onto that same row for display. Since
// stripFlagDisplayFields drops summaryDescription as display-only on every
// save, that text would otherwise be silently and permanently lost the
// moment the record is next saved (confirmed 2026-09-10: an invDesc-less
// flag round-tripped through Warrants save came back with summaryDescription
// blank on the next flags/jacket/{jacketId} GET). Backfill invDesc from it
// first, stripping the "[Type].  " sub-type prefix a Danger/Gang/Medical/
// Notes summaryDescription carries (see getFlagTableRows) so the raw
// description field doesn't end up with the type label baked into it. Never
// overwrites a real invDesc already on the row (e.g. a Warrant's own
// auto-generated "Warrant #X was issued..." text).
const backfillInvDescFromSummary = (inv) => {
  if (!inv || inv.invDesc || !inv.summaryDescription) return inv;

  const flagTypeConstant = getRefListItemsbyName("FlagTypes")?.find(
    (f) =>
      f?.refListItemGlobalId ===
      pick(inv, "flagTypeID", "flagTypeId", "FlagType_ID"),
  )?.constantName;

  const subType = getFlagSubTypeLabel(flagTypeConstant, inv);
  let invDesc = inv.summaryDescription;
  if (subType) {
    const prefixRe = new RegExp(
      `^${subType.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\.\\s*`,
    );
    invDesc = invDesc.replace(prefixRe, "");
  }

  return invDesc ? { ...inv, invDesc } : inv;
};

// Mirrors ASP's Bio.Last_Comma_First virtual field:
// "[Bio||LName], [Bio||FName] [Bio||MName] [Bio||vfSuffix]"
const lastCommaFirst = (bio) => {
  if (!bio?.lName) return "";
  let name = `${bio.lName}, ${bio.fName || ""}`.trim();
  if (bio.mName) name += ` ${bio.mName}`;
  if (bio.suffix) name += `, ${bio.suffix}`;
  return name;
};

// Mirrors ASP's Bookings.Summary_Description virtual field exactly:
// Inmate_Description = "[Bio||Last_Comma_First] (#[Bookings||BookingID])"
// vfArrestDateTime    = ".  Arrested on [ArrestReports||Arrest_Date]." (only when set)
const bookingRecordSummary = (bookings) => {
  const bio = bookings?.bio?.[0];
  // The displayed "#" is the booking number (what the header shows and what
  // the backend's own relatedRecordSummary uses, e.g. #2604794), not the
  // internal bookingId primary key (e.g. 411543).
  const bookingId = pick(bookings, "bookingNumber", "bookingId");
  const name = lastCommaFirst(bio);
  if (!name && !bookingId) return "";

  const idPart = bookingId ? ` (#${bookingId})` : "";
  const head = `${name}${idPart}`.trim();
  if (!head) return "";

  const arrestDate = pick(bookings?.arrestReports?.[0], "arrestDate");
  return arrestDate ? `${head}.  Arrested on ${arrestDate}.` : `${head}.`;
};

// True when dateStr ("MM/DD/YYYY") is today or later - i.e. the flag hasn't
// expired yet. Written without a dayjs format-parse (this file has no dayjs
// dependency and invExpireDate is always stamped "MM/DD/YYYY" by FolderFlags).
const isTodayOrFutureDate = (dateStr) => {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(dateStr || ""));
  if (!match) return false;
  const [, m, d, y] = match;
  const parsed = new Date(Number(y), Number(m) - 1, Number(d));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsed >= today;
};

// The flag's expiration date, but only when it's still in the future -
// PCI's Gang/Medical/Notes flags show this only while it hasn't lapsed yet.
export const getFutureExpirationDate = (item) => {
  const expireDate = pick(item, "invExpireDate", "INV_EXPIRE_DATE");
  return expireDate && isTodayOrFutureDate(expireDate) ? expireDate : "-";
};

// True when this cdINVOLVEMENTS row is the auto-created Outstanding Warrant
// flag itself (flagTypeID FLAGTYPES_WARRANTS), as opposed to some other flag
// type that merely carries relatedObjectType "Warrants" because it was added
// while viewing the record from the Warrants module.
const isWarrantsFlag = (item) => {
  const warrantsFlagTypeId = getRefListItemsbyName("FlagTypes")?.find(
    (f) => f?.constantName === "FLAGTYPES_WARRANTS",
  )?.refListItemGlobalId;
  if (warrantsFlagTypeId == null) return false;
  return pick(item, "flagTypeID", "flagTypeId", "FlagType_ID") === warrantsFlagTypeId;
};

// Mirrors ASP's Related Record Summary for a Danger/Gang/Medical/Notes flag
// added from the Warrants module: "Warrant Root in Jacket for [FName]
// [MName] [LName], [Suffix]." - note the first-name-first order, unlike
// lastCommaFirst's "Last, First" used everywhere else.
const warrantRootJacketSummary = (bio) => {
  if (!bio?.fName && !bio?.lName) return "";
  let name = [bio?.fName, bio?.mName, bio?.lName].filter(Boolean).join(" ");
  if (!name) return "";
  if (bio?.suffix) name += `, ${bio.suffix}`;
  return `Warrant Root in Jacket for ${name}.`;
};

// Mirrors ASP's Medical/Suicide/InmateClassificationQuestionnaires
// Summary_Description virtual field:
// "[Label] given on [Date_Added] at [Time_Added] to [...\Bookings||Summary_Description]"
const questionnaireRecordSummary = (label, item, bookings) => {
  if (!item) return "";
  const date = pick(item, "dateAdded", "Date_Added");
  const time = pick(item, "timeAdded", "Time_Added");

  let text = label;
  if (date) text += ` given on ${date}`;
  if (time) text += ` at ${time}`;

  const bookingSummary = bookingRecordSummary(bookings);
  return bookingSummary ? `${text} to ${bookingSummary}` : `${text}.`;
};

// Finds the questionnaire a flag points to. A saved questionnaire is matched
// by its real ID; a brand-new one being added in the same session (Gang flag
// added straight off the Classifications tab, before Save assigns a real ID)
// has no ID yet on either side, so falls back to the one draft entry in the
// list that's likewise still ID-less - there's only ever one being edited at
// a time.
const findQuestionnaire = (list, relatedId, ...idKeys) => {
  if (!list?.length) return undefined;
  if (relatedId !== undefined && relatedId !== null && relatedId !== "") {
    return list.find((q) => String(pick(q, ...idKeys)) === String(relatedId));
  }
  return list.find((q) => pick(q, ...idKeys) === undefined);
};

// Recovers the link between a saved flag and the questionnaire that
// triggered it, for a flag whose RelatedObjectType comes back "Bookings"
// even though it was originally tagged to a specific questionnaire - the
// backend's Save currently resets RelatedObjectType/RelatedObjectID to
// "Bookings"/bookingId for every non-In-Jail flag, discarding whatever the
// frontend sent. Both the flag's invCreatedDate and the questionnaire's
// dateAdded/timeAdded are stamped by the same client action (one dayjs()
// call chain, at Save), so matching them down to the minute reliably
// recovers the pairing without needing that database-level link.
const findQuestionnaireByCreatedTime = (list, invCreatedDate) => {
  if (!list?.length || !invCreatedDate) return undefined;
  const flagMinute = String(invCreatedDate).slice(0, 16); // "MM/DD/YYYY HH:mm"
  if (flagMinute.length < 16) return undefined;
  return list.find((q) => {
    const date = pick(q, "dateAdded", "Date_Added");
    const time = pick(q, "timeAdded", "Time_Added");
    return date && time && `${date} ${time}` === flagMinute;
  });
};

// Resolves a cdINVOLVEMENTS row's RelatedObjectType/RelatedObjectID (the
// polymorphic pointer to whatever record caused the flag - a booking, a
// warrant, or a booking-intake questionnaire) into a readable summary.
// Mirrors the ASP "Related_Record_Summary" virtual field.
export const getRelatedRecordSummary = (item, bookings) => {
  if (!item) return "";
  const relatedType = pick(item, "relatedObjectType", "RelatedObjectType");
  const relatedId = pick(item, "relatedObjectID", "relatedObjectId", "RelatedObjectID");

  switch (relatedType) {
    case "Warrants": {
      // relatedObjectType "Warrants" covers two different situations that
      // need different text:
      //  - the flag actually IS the auto-created Outstanding Warrant flag
      //    (see pages/Warrants/index.jsx) - show its own invDesc verbatim
      //    ("Warrant #X was issued on DATE. The status is STATUS.").
      //  - a Danger/Gang/Medical/Notes flag added while viewing the record
      //    from the Warrants module - FolderFlags.jsx stamps those with the
      //    same relatedObjectType "Warrants" (see startingPoint === "Warrants"
      //    there) even though they aren't warrant flags themselves. ASP shows
      //    these as "Warrant Root in Jacket for [First] [Middle] [Last],
      //    [Suffix]." instead.
      if (isWarrantsFlag(item)) {
        if (item?.invDesc) return item.invDesc;
        const warrant = bookings?.warrants?.find(
          (w) => String(pick(w, "warrantId", "warrantID")) === String(relatedId),
        );
        const name = lastCommaFirst(bookings?.bio?.[0]);
        if (!warrant) return name ? `${name}.` : bookingRecordSummary(bookings);
        const num = pick(warrant, "warrantId", "warrantID");
        const offense = pick(warrant, "offenseCode");
        const warrantPart = `Warrant #${num}${offense ? ` - ${offense}` : ""}`;
        return name ? `${name}.  ${warrantPart}.` : `${warrantPart}.`;
      }
      return warrantRootJacketSummary(bookings?.bio?.[0]) || bookingRecordSummary(bookings);
    }
    case "CivilPapersPeople":
      // "Civil Paper #94407.  Civil Complaint (Summons and Complaint)." —
      // stamped on the flag's invDesc when the Civil flag is created.
      return item?.invDesc || "";
    case "MedicalQuestionnaires": {
      const q = findQuestionnaire(
        bookings?.medicalQuestionnaires,
        relatedId,
        "medicalQuestionnaireId",
        "MedicalQuestionnaireID",
      );
      return questionnaireRecordSummary("Medical Questionnaire", q, bookings);
    }
    case "InmateClassificationQuestionnaires": {
      const q = findQuestionnaire(
        bookings?.inmateClassificationQuestionnaires,
        relatedId,
        "inmateClassificationQuestionnaireID",
        "inmateClassificationQuestionnaireId",
        "InmateClassificationQuestionnaireID",
      );
      return questionnaireRecordSummary("Inmate Classification Questionnaire", q, bookings);
    }
    case "SuicideQuestionnaires": {
      const q = findQuestionnaire(
        bookings?.suicideQuestionnaires,
        relatedId,
        "suicideQuestionnaireId",
        "SuicideQuestionnaireID",
      );
      return questionnaireRecordSummary("Suicide Questionnaire", q, bookings);
    }
    case "Bookings":
    case "WarrantRoots": {
      // The backend forces every non-In-Jail flag's RelatedObjectType to
      // "Bookings" on Save (see comment on findQuestionnaireByCreatedTime),
      // so a flag that really was triggered by a questionnaire lands here
      // too. Try to recover that pairing by creation-time match before
      // falling back to the plain booking summary.
      const invCreatedDate = pick(item, "invCreatedDate", "INV_CREATED_DATE");
      const classificationMatch = findQuestionnaireByCreatedTime(
        bookings?.inmateClassificationQuestionnaires,
        invCreatedDate,
      );
      if (classificationMatch) {
        return questionnaireRecordSummary(
          "Inmate Classification Questionnaire",
          classificationMatch,
          bookings,
        );
      }
      const medicalMatch = findQuestionnaireByCreatedTime(
        bookings?.medicalQuestionnaires,
        invCreatedDate,
      );
      if (medicalMatch) {
        return questionnaireRecordSummary("Medical Questionnaire", medicalMatch, bookings);
      }
      const suicideMatch = findQuestionnaireByCreatedTime(
        bookings?.suicideQuestionnaires,
        invCreatedDate,
      );
      if (suicideMatch) {
        return questionnaireRecordSummary("Suicide Questionnaire", suicideMatch, bookings);
      }
      return bookingRecordSummary(bookings);
    }
    default:
      return "";
  }
};

// The ASP grid's "PrimaryObjectFieldSource" column - the literal relation
// path (e.g. "cdINVOLVEMENTS\Bookings") that Related_Record_Summary resolved
// through. Just RelatedObjectType prefixed with the owning table name.
export const getPrimaryObjectFieldSource = (item) => {
  const relatedType = pick(item, "relatedObjectType", "RelatedObjectType");
  return relatedType ? `cdINVOLVEMENTS\\${relatedType}` : "-";
};

// Builds the full 11-column row set from ASP's columnsFlagInstancesFullList
// (FlagListInterface.asp): Flag / Summary Description / Related Record
// Summary / Expiration Date / ID (INV_OBJ_ID) / LinkedObjectType / FlagType
// ID / INV ID / Related Object ID / RelatedObjectType /
// PrimaryObjectFieldSource. In ASP the last 7 are width-0 (collapsed) by
// default - internal plumbing fields, not normally user-facing - but are
// included here to faithfully match what the client compared against.
//
// Verified against ASP's "Flags -- Webpage Dialog": Summary Description is
// "[Type].  [Description]" for Danger/Gang/Medical/Notes (the flag types
// with a sub-type ref list); Warrants/Civil/In Jail have no sub-type and
// their Summary Description is always blank, even though Warrants' invDesc
// is populated - that text surfaces in Related Record Summary instead (see
// getRelatedRecordSummary's "Warrants" case). Related Record Summary itself
// is always the polymorphic RelatedObjectType/RelatedObjectID resolution -
// for Danger/Gang/Medical/Notes/In Jail that resolves through
// relatedObjectType "Bookings" straight to the person's booking summary, not
// the flag's own Description text.
export const getFlagTableRows = (cdInvolvements, flagTypeConstant, flagLabel, bookings) => {
  const relevant = getRelevantInvolvements(cdInvolvements, flagTypeConstant);
  const hasSubType = !!getFlagSubTypeFieldInfo(flagTypeConstant);
  return relevant.map((item, index) => {
    const subType = getFlagSubTypeLabel(flagTypeConstant, item);
    const locallyComputed = hasSubType
      ? [subType, item.invDesc].filter(Boolean).join(". ")
      : "";
    // The backend's computed summaryDescription is only the flag's own
    // description text (e.g. "Medical Person"), without the "[Type]. "
    // sub-type prefix ASP shows - so a Medical Questionnaire flag read
    // "Diabetic. Medical Person" before Save but just "Medical Person" after.
    // Prepend the sub-type label unless the backend already included it.
    const backendSummary = pick(item, "summaryDescription");
    const withSubTypePrefix =
      backendSummary && subType && !String(backendSummary).startsWith(subType)
        ? `${subType}. ${backendSummary}`
        : backendSummary;
    return {
      // A flag added this session (not yet saved) has no invId - key it by
      // type + position instead, unique across the combined all-types list
      // (a bare index collided with nothing and left the row unselectable).
      id: item.invId ?? `new-${flagTypeConstant}-${index}`,
      // The underlying cdINVOLVEMENTS row, so the Flags dialog can resolve
      // a selection without relying on invId (not a rendered column).
      source: item,
      flag: flagLabel,
      // The backend now stamps a computed summaryDescription straight onto
      // each cdINVOLVEMENTS row it returns from flags/jacket/{jacketId} -
      // use that directly when present, since invDesc isn't reliably
      // populated on a record's own saved cdInvolvements (e.g. a Warrant's
      // own cdInvolvements never carries it at all). Recompute locally only
      // as a fallback, for a brand-new, not-yet-saved flag that has no
      // backend-computed value yet. Only for the sub-typed flag types
      // (Danger/Gang/Medical/Notes) - Warrants/Civil/In Jail stay blank
      // here even though the backend's own summaryDescription is non-null
      // for e.g. Warrants, since that text belongs in Related Record
      // Summary instead (see the comment above this function).
      summaryDescription: hasSubType
        ? withSubTypePrefix || locallyComputed || "-"
        : "-",
      // The backend now stamps a computed relatedRecordSummary straight onto
      // each saved cdINVOLVEMENTS row - use that directly when present.
      // getRelatedRecordSummary is still needed as a fallback for a
      // brand-new, not-yet-saved flag (added this session, before Save),
      // which has no backend-computed value yet.
      //
      // Civil flag exception: the backend puts "Civil Paper #220503.  Civil
      // Complaint (Summons)." in its summaryDescription and a "Case Number
      // ... was added on ..." sentence in relatedRecordSummary; the column
      // must show the former (Summary Description stays blank for Civil,
      // above). Other flag types are untouched.
      relatedRecordSummary:
        (flagTypeConstant === "FLAGTYPES_DOCUMENTS" &&
          pick(item, "summaryDescription")) ||
        pick(item, "relatedRecordSummary") ||
        getRelatedRecordSummary(item, bookings) ||
        "-",
      expirationDate: getFutureExpirationDate(item),
      objId: pick(item, "invObjId", "INV_OBJ_ID") ?? "-",
      linkedObjectType: pick(item, "linkedObjectType", "LinkedObjectType") ?? "-",
      flagTypeId: pick(item, "flagTypeID", "flagTypeId", "FlagType_ID") ?? "-",
      invId: pick(item, "invId", "INV_ID") ?? "-",
      relatedObjectId:
        pick(item, "relatedObjectID", "relatedObjectId", "RelatedObjectID") ?? "-",
      relatedObjectType: pick(item, "relatedObjectType", "RelatedObjectType") ?? "-",
      primaryObjectFieldSource: getPrimaryObjectFieldSource(item),
    };
  });
};

export const FLAG_TYPE_LABELS = {
  FLAGTYPES_WARRANTS: "Warrants",
  FLAGTYPES_DOCUMENTS: "Civil",
  FLAGTYPES_DANGER: "Danger",
  FLAGTYPES_INJAIL: "In Jail",
  FLAGTYPES_GANG: "Gang",
  FLAGTYPES_MEDICAL: "Medical",
  FLAGTYPES_NOTES: "Notes",
};

// Mirrors ASP's FlagListInterface.asp DeleteSubObjectAllowed: these three
// flag types are cleared by the record they came from (serving the Warrant /
// Civil Paper, releasing the Inmate), never by hand from the Flags dialog.
// Returns the message to show, or undefined when the flag may be deleted.
const FLAG_DELETE_BLOCKED_MESSAGES = {
  FLAGTYPES_WARRANTS:
    "You cannot delete Warrant flags manually. To clear a Warrant flag, you must serve the Warrant.",
  FLAGTYPES_DOCUMENTS:
    "You cannot delete Civil Paper flags manually. To clear a Civil Paper flag, you must serve the Civil Paper.",
  FLAGTYPES_INJAIL:
    "You cannot delete In Jail flags manually. To clear an In Jail flag, you must release the Inmate.",
};

export const getFlagDeleteBlockedMessage = (flagTypeConstant) =>
  FLAG_DELETE_BLOCKED_MESSAGES[flagTypeConstant];

const ALL_FLAG_TYPE_CONSTANTS = Object.keys(FLAG_TYPE_LABELS);

// Given one cdINVOLVEMENTS row, determine which flag-type constant it
// belongs to - the inverse of getRelevantInvolvements. Needed once the
// "Flags" dialog shows every type combined, since a selected row's own
// type can differ from whichever flag icon was clicked to open it.
export const getInvolvementFlagTypeConstant = (item) => {
  if (!item) return null;
  return (
    ALL_FLAG_TYPE_CONSTANTS.find(
      (constant) => getRelevantInvolvements([item], constant).length > 0,
    ) || null
  );
};

// All active flags across every type - mirrors the ASP "Flags -- Webpage
// Dialog", which lists every flag (Danger, Gang, In Jail, ...) together
// rather than filtering to whichever icon was clicked.
export const getAllFlagTableRows = (cdInvolvements, bookings) => {
  return ALL_FLAG_TYPE_CONSTANTS.flatMap((constant) =>
    getFlagTableRows(cdInvolvements, constant, FLAG_TYPE_LABELS[constant], bookings),
  );
};

// Mirrors ASP's HandleInJailFlag: the In Jail flag reflects current custody
// status rather than being a permanent record - PCI removes it (a hard
// delete, not an expire) once a booking is released, so it doesn't stay
// active forever. Filters structurally (flagTypeID + invObjId) rather than
// by invId, since a just-added, not-yet-saved flag has no invId yet.
// Returns both the filtered array (for the redux payload) and the removed
// rows (so the caller can queue a DELETE for any that were already
// persisted - a same-session, not-yet-saved row has no invId to delete).
export const removeInJailFlag = (cdInvolvements, bookingId) => {
  if (!cdInvolvements?.length) {
    return { cdInvolvements: cdInvolvements || [], removed: [] };
  }
  const inJailRefId = getRefListItemsbyName("FlagTypes")?.find(
    (f) => f?.constantName === "FLAGTYPES_INJAIL",
  )?.refListItemGlobalId;
  if (inJailRefId == null) {
    return { cdInvolvements, removed: [] };
  }

  const isInJailForBooking = (inv) =>
    inv.flagTypeID === inJailRefId &&
    (bookingId == null || inv.invObjId === bookingId);

  return {
    cdInvolvements: cdInvolvements.filter((inv) => !isInJailForBooking(inv)),
    removed: cdInvolvements.filter(isInJailForBooking),
  };
};
